// Logic chèn CÚ PHÁP KHỐI (markdown-lite v2) cho ô soạn nội dung bài Kênh Cộng
// Đồng: tiêu đề `## `, gạch đầu dòng `- `, danh sách số `1. `, kẻ ngang `---`,
// ảnh chèn giữa bài `![N]`, link có chữ hiển thị `[chữ](url)`.
//
// Renderer: mobile/src/modules/community-channel/utils/community-content-blocks.ts
// + frontend/src/pages/Community/community-content-blocks.js — sửa luật ở đây
// PHẢI sửa cả 2 nơi đó.
//
// Mọi hàm trả về "mô tả chỉnh sửa" để toolbar chèn qua execCommand (giữ Ctrl+Z):
//   { from, to, text, selectionStart, selectionEnd }
// nghĩa là thay đoạn [from, to) trong nội dung bằng `text`, rồi đặt vùng chọn.

const URL_REGEX = /^(?:https?:\/\/|www\.)\S+$/i;

/** Mốc đầu/cuối của các dòng chạm vùng chọn [start, end) */
function getLineRange(value, start, end) {
    const from = value.lastIndexOf("\n", start - 1) + 1;
    const nextBreak = value.indexOf("\n", end);
    return { from, to: nextBreak === -1 ? value.length : nextBreak };
}

/**
 * Thêm (hoặc bỏ, nếu đã có) tiền tố đầu dòng cho mọi dòng chạm vùng chọn.
 * @param {(lineIndex: number) => string} makePrefix vd `() => "- "`, `(i) => `${i + 1}. ``
 * @param {RegExp} matchPrefix nhận diện tiền tố đã có để bấm lần 2 là bỏ
 */
export function buildLinePrefixInsert(value, start, end, makePrefix, matchPrefix) {
    const safeValue = value || "";
    const { from, to } = getLineRange(safeValue, start ?? 0, end ?? start ?? 0);
    const lines = safeValue.slice(from, to).split("\n");
    const allPrefixed = lines.every((line) => matchPrefix.test(line));

    const nextLines = lines.map((line, i) =>
        allPrefixed ? line.replace(matchPrefix, "") : `${makePrefix(i)}${line.replace(matchPrefix, "")}`
    );
    const text = nextLines.join("\n");
    return { from, to, text, selectionStart: from + text.length, selectionEnd: from + text.length };
}

/** Chèn một đoạn text tại con trỏ (thay vùng bôi đen nếu có) */
export function buildTextInsert(value, start, end, text, selectOffset) {
    const safeValue = value || "";
    const from = Math.max(0, Math.min(start ?? 0, safeValue.length));
    const to = Math.max(from, Math.min(end ?? from, safeValue.length));
    const selection = selectOffset
        ? { selectionStart: from + selectOffset[0], selectionEnd: from + selectOffset[1] }
        : { selectionStart: from + text.length, selectionEnd: from + text.length };
    return { from, to, text, ...selection };
}

/** Kẻ ngang: luôn nằm trên dòng riêng */
export function buildDividerInsert(value, start, end) {
    const safeValue = value || "";
    const leadBreak = start > 0 && safeValue[start - 1] !== "\n" ? "\n" : "";
    return buildTextInsert(safeValue, start, end, `${leadBreak}---\n`);
}

/** Ảnh thứ N của bài, luôn nằm trên dòng riêng */
export function buildImageInsert(value, start, end, imageIndex) {
    const safeValue = value || "";
    const leadBreak = start > 0 && safeValue[start - 1] !== "\n" ? "\n" : "";
    return buildTextInsert(safeValue, start, end, `${leadBreak}![${imageIndex}]\n`);
}

/**
 * Link có chữ hiển thị. Bôi đen URL → chờ gõ CHỮ; bôi đen chữ → chờ dán URL;
 * không bôi đen → chèn mẫu và bôi đen sẵn phần chữ.
 */
export function buildLinkInsert(value, start, end) {
    const safeValue = value || "";
    const selected = safeValue.slice(start ?? 0, end ?? start ?? 0).trim();

    if (URL_REGEX.test(selected)) {
        const text = `[chữ hiển thị](${selected})`;
        return buildTextInsert(safeValue, start, end, text, [1, 1 + "chữ hiển thị".length]);
    }
    if (selected) {
        const text = `[${selected}](https://)`;
        return buildTextInsert(safeValue, start, end, text, [text.length - 1, text.length - 1]);
    }
    const text = "[chữ hiển thị](https://)";
    return buildTextInsert(safeValue, start, end, text, [1, 1 + "chữ hiển thị".length]);
}

export const HEADING_PREFIX_REGEX = /^#{2,3}\s+/;
export const BULLET_PREFIX_REGEX = /^[-•]\s+/;
export const NUMBERED_PREFIX_REGEX = /^\d{1,2}[.)]\s+/;
